/**
 * How full a route is, decided once.
 *
 * A route's load factor is not the count of loading units: it is what those units come to in UK
 * pallet equivalents against the vehicle's UK pallet capacity. A sort bag is 3.43 of them by
 * default and a cage 1.67, so a route can look comfortably full on unit counts and be over
 * capacity in the space it actually occupies.
 */
import { CollectionsConfig, LoadingUnitType, VehicleType } from "@/types/collections"
import {
  CapacityStop,
  calculateVehicleCapacity,
  stopLoadingUnits,
} from "@/lib/routeCostCalculation"

export interface UkPalletRatios {
  sort_bags: number
  cages: number
  magnums: number
  eur_pallets: number
  uk_pallets: number
}

/** Used when config has not loaded or the column is null. */
export const UK_PALLET_RATIO_DEFAULTS: UkPalletRatios = {
  sort_bags: 3.43,
  cages: 1.67,
  magnums: 1.0,
  eur_pallets: 1.25,
  uk_pallets: 1.0,
}

type RatioConfig = Pick<
  CollectionsConfig,
  | "uk_pallet_ratio_sort_bag"
  | "uk_pallet_ratio_cage"
  | "uk_pallet_ratio_magnum"
  | "uk_pallet_ratio_eur_pallet"
  | "uk_pallet_ratio_uk_pallet"
>

/** The configured ratios, falling back per field rather than all-or-nothing. */
export function ukPalletRatios(config?: Partial<RatioConfig> | null): UkPalletRatios {
  return {
    sort_bags: config?.uk_pallet_ratio_sort_bag ?? UK_PALLET_RATIO_DEFAULTS.sort_bags,
    cages: config?.uk_pallet_ratio_cage ?? UK_PALLET_RATIO_DEFAULTS.cages,
    magnums: config?.uk_pallet_ratio_magnum ?? UK_PALLET_RATIO_DEFAULTS.magnums,
    eur_pallets: config?.uk_pallet_ratio_eur_pallet ?? UK_PALLET_RATIO_DEFAULTS.eur_pallets,
    uk_pallets: config?.uk_pallet_ratio_uk_pallet ?? UK_PALLET_RATIO_DEFAULTS.uk_pallets,
  }
}

const RATIO_KEY_BY_UNIT: Partial<Record<LoadingUnitType, keyof UkPalletRatios>> = {
  "Sort Bags": "sort_bags",
  Cages: "cages",
  Magnums: "magnums",
  "Pallets (EUR)": "eur_pallets",
  "Pallets (UK)": "uk_pallets",
}

/** A count of one loading unit in UK pallet equivalents. Loose load and an unknown type count 1:1. */
export function toUkPallets(
  units: number,
  unitType: LoadingUnitType | string | null | undefined,
  config?: Partial<RatioConfig> | null,
): number {
  if (!units) return 0
  const key = unitType ? RATIO_KEY_BY_UNIT[unitType as LoadingUnitType] : undefined
  return key ? units * ukPalletRatios(config)[key] : units
}

/**
 * One stop's load in UK pallet equivalents, by the rule a route's load factor adds up.
 *
 * So the stops of a route sum to `routeCapacity().ukPalletsUsed`. That is why loose load and an
 * unknown unit come to nothing here, where `toUkPallets` counts them 1:1: a route's capacity
 * has no column for them either.
 */
export function stopUkPallets(
  stop: CapacityStop,
  {
    locations,
    config,
  }: Pick<RouteLoadFactorParams, "locations" | "config"> = {},
): number {
  const { units, unitType } = stopLoadingUnits(stop, locations)
  const key = unitType ? RATIO_KEY_BY_UNIT[unitType] : undefined
  return key ? units * ukPalletRatios(config)[key] : 0
}

export interface ToCapacityStopOptions {
  /**
   * Overrides whatever volume the stop carries. The planning views read their
   * forecast from the slot rather than the stop, because a planned stop's own
   * volume is 0 until an instance has been generated.
   */
  volume?: number | null
  /**
   * Count what was collected rather than what was forecast, where a stop has
   * both. Monitoring wants this and planning does not: one is reporting the day
   * that happened, the other is sizing the day that has not.
   */
  preferActuals?: boolean
}

/**
 * A stop as capacity sees it.
 *
 * Both spellings of the type are read for the reason given on `CapacityStop`: a
 * Stop and a PlannedStop carry `slotType`, a StopInstance carries `stop_type`.
 */
export function toCapacityStop(
  stop: {
    slotType?: string
    stop_type?: string
    locationId?: string
    location_id?: string
    location?: CapacityStop["location"]
    volumeEstimated?: number | null
    volume_forecast?: number | null
    volumeActual?: number | null
    volume_actual?: number | null
    loadingUnitsType?: CapacityStop["loadingUnitsType"]
    loading_unit_type?: CapacityStop["loadingUnitsType"]
    loadingUnitsActual?: number | null
    loadingUnitsEstimated?: number | null
    loading_units_forecast?: number | null
  },
  { volume, preferActuals }: ToCapacityStopOptions = {},
): CapacityStop {
  const volumeActual = stop.volumeActual ?? stop.volume_actual ?? 0
  const volumeForecast = stop.volumeEstimated ?? stop.volume_forecast ?? 0
  const unitsActual = stop.loadingUnitsActual ?? 0
  const unitsForecast = stop.loadingUnitsEstimated ?? stop.loading_units_forecast ?? 0

  // A stop that has been served reports what was actually collected; one that
  // has not has only the forecast to offer.
  const hasActuals = preferActuals === true && (unitsActual > 0 || volumeActual > 0)

  return {
    slotType: stop.slotType,
    stop_type: stop.stop_type,
    locationId: stop.locationId ?? stop.location_id,
    location: stop.location,
    volumeEstimated: volume ?? (hasActuals ? volumeActual : volumeForecast),
    volumeActual,
    loadingUnitsType: stop.loadingUnitsType ?? stop.loading_unit_type,
    // Withheld unless actuals were asked for. `calculateVehicleCapacity` takes any non-zero
    // `loadingUnitsActual` in preference to everything else, so passing it through on a forecast
    // reading would quietly turn it into an actual one.
    loadingUnitsActual: preferActuals ? unitsActual : undefined,
    loadingUnitsEstimated: hasActuals ? unitsActual : unitsForecast,
  }
}

export interface RouteLoadFactorParams {
  vehicleType: VehicleType | null | undefined
  stops: CapacityStop[]
  locations?: Array<{
    id: string
    loading_unit_type?: CapacityStop["loadingUnitsType"]
    parcels_per_loading_unit?: number
  }>
  config?: Partial<RatioConfig> | null
}

export interface RouteCapacity {
  /** What the route's stops come to in UK pallet equivalents. */
  ukPalletsUsed: number
  /** The vehicle's capacity in the same unit. 0 when none is recorded. */
  ukPalletCapacity: number
  /** The unweighted fill across all unit types, from `calculateVehicleCapacity`. */
  totalFillPercentage: number
}

/**
 * A route's load in UK pallet equivalents, and the capacity to measure it
 * against.
 *
 * Separate from `routeLoadFactor` because the map view sums used and capacity
 * across every visible route and divides once at the end. A mean of per-route
 * percentages is not the same number, and would let a nearly empty van count as
 * much as a full HGV.
 *
 * `null` when there is no vehicle type -- an unknown load and an empty route are
 * not the same thing.
 */
export function routeCapacity({
  vehicleType,
  stops,
  locations,
  config,
}: RouteLoadFactorParams): RouteCapacity | null {
  const capacityInfo = calculateVehicleCapacity(vehicleType, stops, locations)
  if (!capacityInfo) return null

  const ratios = ukPalletRatios(config)
  const used = capacityInfo.usedCapacity

  return {
    ukPalletsUsed:
      used.uk_pallets * ratios.uk_pallets +
      used.eur_pallets * ratios.eur_pallets +
      used.cages * ratios.cages +
      used.magnums * ratios.magnums +
      used.sort_bags * ratios.sort_bags,
    ukPalletCapacity: vehicleType?.uk_pallet_capacity || 0,
    totalFillPercentage: capacityInfo.totalFillPercentage,
  }
}

/**
 * Percentage of the vehicle's UK pallet capacity the route's stops come to.
 *
 * Falls back to `totalFillPercentage` when the vehicle has no UK pallet capacity recorded: without
 * a denominator in UK pallets there is nothing to express the ratios against.
 */
export function routeLoadFactor(params: RouteLoadFactorParams): number | null {
  const capacity = routeCapacity(params)
  if (!capacity) return null
  if (capacity.ukPalletCapacity <= 0) return capacity.totalFillPercentage
  return (capacity.ukPalletsUsed / capacity.ukPalletCapacity) * 100
}
